import { McdViewError } from '~/lib/models/errors'
import { CategoryListMdw } from '~/lib/services/middleware/middleware.dtos'

export const useMenu = () => {
  const appStore = useAppStore()
  const route = useRoute()

  const { slug } = route.params

  const [categorySlug, productSlug] = Array.isArray(slug) ? slug : []
  const { staticMenuRoute, staticCategoryRoute, staticProductRoute } = useMcdRoutes()
  const { middlewareServiceRef } = useStoreService()
  const middlewareService = middlewareServiceRef()

  const fetchStaticCategories = async () => {
    try {
      // Request this on client if not loaded previously on server
      if (!appStore.staticCategories || !appStore.staticCategories.length) {
        // mdw static categories
        const { data: categoriesListMdw } = await middlewareService.fetchStaticCategories()
        appStore.staticCategories = categoriesListMdw
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('error loading all categories', e)
      appStore.staticCategories = []
    }

    return true
  }

  const loadCategory = async (cat: CategoryListMdw) => {
    try {
      if (cat.combo) return await middlewareService.fetchCategoryDetailCombo(cat)
      return await middlewareService.fetchCategoryDetailList(cat)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('error loading cat detail', e)
      return undefined
    }
  }

  const getRedirectionRoute = async () => {
    await fetchStaticCategories()

    // go to menu
    if (!categorySlug && !productSlug) return staticMenuRoute()

    const categories = appStore.staticCategories!
    const categoryOnList = categories.find(cat => slugify(cat.name) === categorySlug)
    // no category with this slug founded!
    if (!categoryOnList) return staticMenuRoute()

    // category founded so redirect to /menu/<cat>
    if (categoryOnList && !productSlug) return staticCategoryRoute(categoryOnList.name)

    // find catDetail products from category
    const catDetail = await loadCategory(categoryOnList)
    if (!catDetail) return staticMenuRoute()

    if (productSlug) {
      // find prod from slug on categoryDetail
      const product = catDetail.products.find(prod => slugify(prod.name) === productSlug)
      if (!product) return staticCategoryRoute(categoryOnList.name)
      return staticProductRoute(categoryOnList.name, product.name)
    }
  }

  const loadCategoryData = async (catSlug: string) => {
    await fetchStaticCategories()

    const categories = appStore.staticCategories!
    const categoryOnList = categories.find(cat => slugify(cat.name) === catSlug)
    if (!categoryOnList) {
      throw new McdViewError('No categoryList with this slug was found', 404, staticMenuRoute())
    }
    // find catDetail products from category
    const catDetail = await loadCategory(categoryOnList)
    if (!catDetail) {
      const err = new McdViewError(
        'Failed to fetch cat detail from catId = ' + categoryOnList._id,
        404,
        staticMenuRoute()
      )
      throw err
    }

    return {
      categories,
      category: catDetail,
    }
  }

  const loadProductData = async (catSlug: string, prodSlug: string) => {
    await fetchStaticCategories()

    try {
      const { category, categories } = await loadCategoryData(catSlug)
      // find prod from slug on categoryDetail
      const product = category.products.find(prod => slugify(prod.name) === prodSlug)
      if (!product) {
        throw new McdViewError(
          'No product with this slug was found in category',
          404,
          staticCategoryRoute(category.name)
        )
      }

      // Visiting product on combo category
      if (category.combo) {
        const parentCategoryId = product.category
        const matchedParentCat = categories.find(c => c._id === parentCategoryId)
        if (matchedParentCat) {
          throw new McdViewError(
            'Request product on combo category, redirecting to current category',
            301,
            staticProductRoute(matchedParentCat.name, product.name)
          )
        }
      }

      return {
        categoryName: category.name,
        product,
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log('🚀 ~ file: useMenu.ts:98 ~ loadProductData ~ error:', error?.message)
      throw error
    }
  }

  return {
    fetchStaticCategories,
    getRedirectionRoute,
    loadCategoryData,
    loadProductData,
  }
}
